/**
 * Item Creation Provenance RPC Client
 *
 * Typed wrapper for the approve_ai_item RPC defined in migration
 * 566_item_creation_provenance.sql. Mirrors delegationRpc.ts's style:
 * a thin async function, p_-prefixed SQL param names, and a
 * { success, error? } return shape.
 */

import { getSupabaseClient } from './supabaseClient';
import type { ItemCreationProvenance } from '../types/prioritization/itemProvenance';

export interface ApproveAiItemResult {
  success: boolean;
  error?: string;
  item?: {
    id: string;
    creationProvenance: ItemCreationProvenance;
    approvedBy?: string;
    approvedAt?: string;
  };
}

interface ApproveAiItemRpcResponse {
  success?: boolean;
  error?: string;
  item_id?: string;
  creation_provenance?: ItemCreationProvenance;
  approved_by?: string;
  approved_at?: string;
}

/**
 * Approve an AI-drafted item, transitioning creation_provenance from
 * 'ai_draft' to 'ai_approved'. Server-side (approve_ai_item RPC) rejects
 * when the item isn't currently 'ai_draft' or the caller isn't authorized.
 */
export async function approveAiItemRpc(
  itemId: string,
  approvedById: string,
): Promise<ApproveAiItemResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('approve_ai_item', {
    p_item_id: itemId,
    p_approved_by: approvedById,
  });

  if (error) return { success: false, error: error.message };

  const result = data as ApproveAiItemRpcResponse | null;
  if (!result) return { success: false, error: 'No response from server' };
  if (!result.success) return { success: false, error: result.error ?? 'Approval failed' };

  return {
    success: true,
    item: result.item_id
      ? {
          id: result.item_id,
          creationProvenance: result.creation_provenance ?? 'ai_approved',
          approvedBy: result.approved_by,
          approvedAt: result.approved_at,
        }
      : undefined,
  };
}
